import { Link } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';

import { Icon } from '@sixam/ui/icon';
import { ChevronRightIcon } from 'lucide-react-native';
import { Text } from '@sixam/ui/text';
import { isCheckInWindowOpen } from '@sixam/shared/event-checkin-core';
import { fetchTenantEvents, type Event } from '@/lib/events';
import { useCurrentTime } from '@/lib/dev-clock';

type ClubCheckInContentProps = Readonly<{
  tenantId: string;
}>;

/**
 * Lists the club's events that are open for check-in so an admin can pick one.
 * Choosing an event opens the full-screen station — the same screen the app
 * opens onto during an event, and the only place scanning happens.
 */
export function ClubCheckInContent({ tenantId }: ClubCheckInContentProps) {
  const now = useCurrentTime();
  const [events, setEvents] = React.useState<Event[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    fetchTenantEvents(tenantId)
      .then((all) => {
        if (!active) {
          return;
        }
        const open = all.filter(
          (event) =>
            event.status === 'published' &&
            event.checkin_mode !== 'none' &&
            isCheckInWindowOpen(event, now)
        );
        setEvents(open);
        setError(null);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load events.');
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });
    return () => {
      active = false;
    };
    // Re-run only when the club changes; `now` ticks constantly and would refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  if (isLoading) {
    return <ActivityIndicator />;
  }

  if (error) {
    return (
      <Text className="text-sm text-destructive" selectable>
        {error}
      </Text>
    );
  }

  if (events.length === 0) {
    return (
      <Text variant="muted">
        No event is open for check-in right now. Check-in opens two hours before an event starts.
      </Text>
    );
  }

  // Picking the event is all this does; the scanning itself happens on the one
  // full-screen station, the same screen every other route opens.
  return (
    <View className="gap-2">
      <Text variant="muted">Choose the event to check runners into.</Text>
      {events.map((event) => (
        <Link
          asChild
          href={{ params: { id: event.id }, pathname: '/admin/check-in/[id]' }}
          key={event.id}
        >
          <Pressable
            accessibilityRole="button"
            className="flex-row items-center justify-between gap-3 rounded-md border border-border p-4 active:opacity-70"
          >
            <Text className="min-w-0 flex-1 font-bold" numberOfLines={2}>
              {event.title}
            </Text>
            <Icon as={ChevronRightIcon} className="size-5 text-muted-foreground" />
          </Pressable>
        </Link>
      ))}
    </View>
  );
}
